// ─────────────────────────────────────────────────────────────────────────────
// CRM 2.0 — useBulkAction
//
// TanStack Query mutation wrapper around the additive `POST /api/v2/bulk/:entity`
// endpoint. Submits { ids, value } for one entity and returns the per-record
// updated/skipped results. On success it invalidates that entity's list query so
// the updated rows refresh in place (Requirements 8.1, 8.2, 8.4).
//
// Uses the shared axios client (cookie auth) exactly like the other feature hooks.
// ─────────────────────────────────────────────────────────────────────────────

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { BULK_LIST_QUERY_KEY } from '../constants';
import type { BulkEntity, BulkResponse } from '../types';

/** Arguments for a single bulk submission. */
export interface BulkActionVars {
  /** The distinct record ids to operate on (server dedupes + chunks; Req 15.4). */
  ids: string[];
  /** The target value for the entity's op field (validated server-side). */
  value: string;
}

/**
 * Returns a mutation that applies one op to many records of `entity`. On success
 * the entity's list cache is invalidated so successfully updated rows re-fetch.
 */
export function useBulkAction(entity: BulkEntity) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ids, value }: BulkActionVars): Promise<BulkResponse> => {
      const { data } = await api.post<BulkResponse>(
        `/api/v2/bulk/${encodeURIComponent(entity)}`,
        { ids, value },
      );
      return data;
    },
    onSuccess: () => {
      // Refresh the list so updated rows show their new status/stage (Req 8.4).
      queryClient.invalidateQueries({ queryKey: [BULK_LIST_QUERY_KEY[entity]] });
    },
  });
}
